import type React from 'react';
import type {AnimatedSprite} from '@Libraries/animated-sprite.library';

export interface ManagePmwStockPanelProps {
  searchTerm: string;
  noKeywordsFilter: boolean;
  panelId?: string;
  showHiddenFilter: boolean | null;
  showPanel: string;
  categoryFilter?: string[] | null;
  sortByOption: string;
  usedForMaskingFilter?: boolean | null;
  areFiltersDisabled?: boolean;
  setAreFiltersDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  setDisableSave: React.Dispatch<React.SetStateAction<boolean>>;
}

export enum ManagePMWStockPanelType {
  PMW_ALL_STOCK = 'PMW-ALL-STOCK',
  PMW_SHAPES = 'PMW-SHAPES',
  PMW_ANIMATED_STICKERS = 'PMW-ANIMATED-STICKERS',
  PMW_STILL_STICKERS = 'PMW-STILL-STICKERS',
  PMW_ICONS = 'PMW-ICONS',
  PMW_EMOJIS = 'PMW-EMOJIS',
  PMW_IMAGES = 'PMW-IMAGES',
  PMW_EXTRACTED_GETTY_STICKERS = 'PMW-EXTRACTED-GETTY_STICKERS',
  PMW_AI_STICKERS = 'PMW-AI-STICKERS',
  PMW_AI_ANIMATED_STICKERS = 'PMW-AI-ANIMATED-STICKERS',
  PMW_AI_ICONS = 'PMW-AI-ICONS',
}

export enum PMWStockItemType {
  SHAPE = 'shape',
  ANIMATED_STICKER = 'animated_sticker',
  STILL_STICKER = 'still_sticker',
  ICON = 'icon',
  EMOJI = 'emoji',
  IMAGE = 'image',
  EXTRACTED_GETTY_STICKER = 'extracted_getty_sticker',
  AI_STICKER = 'ai_sticker',
  AI_ANIMATED_STICKER = 'ai_animated_sticker',
  AI_ICON = 'ai_icon',
}

export enum SortByOption {
  AscendingAdminScore = 'admin_score_asc',
  DescendingAdminScore = 'admin_score_desc',
  AscendingUploadDate = 'uploaded_on_asc',
  DescendingUploadDate = 'uploaded_on_desc',
}

export enum InfoTab {
  KEYWORDS = 'keywords',
  DESCRIPTION = 'description',
}

export interface PMWStockBackendResult {
  l: PMWStockBackendData[];
  total?: number;
}

export interface PMWStockMediaSearchParams {
  isManageappRequest: number;
  st: string;
  types?: string;
  noKeywords: boolean;
  showHidden?: boolean | null;
  isUsedForMasking?: number;
  iconCategories?: string[] | null;
  shapeCategories?: string[] | null;
  sortBy: string;
  limit: number;
  offset: number;
}

export interface PMWStockBackend {
  idPmwStockMedia: string;
  adminScore: number;
  isHidden: number;
  uploadedOn: number;
  keywords: string[];
  description: string;
  isMask: number;
  type: string;
  relevance?: number | null;
}

export interface PMWStockBackendAnimatedSticker extends PMWStockBackend {
  idPMWStockAnimatedSticker: string;
  duration: number;
  frameRate: number;
  hashedFilename: string;
  highResAnimatedSprite: AnimatedSprite;
  screenAnimatedSprite: AnimatedSprite;
  width: number;
  height: number;
}

export interface PMWStockBackendShape extends PMWStockBackend {
  idPMWStockShape: string;
  category: string;
}

export interface PMWStockBackendStillSticker extends PMWStockBackend {
  idPMWStockStillSticker: string;
  hashedFilename: string;
  width: number;
  height: number;
}

export interface PMWStockBackendExtractedGettySticker extends PMWStockBackend {
  idPMWExtractedGettySticker: string;
  hashedFilename: string;
  originalSheetId: string;
  width: number;
  height: number;
}
export interface PMWStockBackendAiSticker extends PMWStockBackend {
  idPMWStockAiSticker: string;
  hashedFilename: string;
  width: number;
  height: number;
}

export interface PMWStockBackendAiAnimatedSticker extends PMWStockBackend {
  idPMWStockAiAnimatedSticker: string;
  hashedFilename: string;
  width: number;
  height: number;
  frameRate: number;
  duration: number;
  highResAnimatedSprite: AnimatedSprite;
  screenAnimatedSprite: AnimatedSprite;
}

export interface PMWStockBackendImage extends PMWStockBackend {
  idPMWStockImage: string;
  hashedFilename: string;
  width: number;
  height: number;
}

export interface PMWStockBackendEmoji extends PMWStockBackend {
  idPMWStockEmoji: string;
  hashedFilename: string;
}

export interface PMWStockBackendIcon extends PMWStockBackend {
  idPMWStockIcon: string;
  hashedFilename: string;
  category: string;
}

export interface PMWStockBackendAiIcon extends PMWStockBackend {
  idPMWStockAiIcon: string;
  hashedFilename: string;
  category: string;
}

export type PMWStockBackendData =
  | PMWStockBackendShape
  | PMWStockBackendIcon
  | PMWStockBackendStillSticker
  | PMWStockBackendImage
  | PMWStockBackendEmoji
  | PMWStockBackendAnimatedSticker
  | PMWStockBackendExtractedGettySticker
  | PMWStockBackendAiSticker
  | PMWStockBackendAiAnimatedSticker
  | PMWStockBackendAiIcon;

export const GRID_ITEM_MAX_WIDTH = 325;
export const GRID_ITEM_ASPECT_RATIO = 2.7;
export const DEFAULT_ITEMS_PER_PAGE = 50;
export const ITEMS_PER_PAGE_OPTIONS = [30, 40, 50, 60, 70, 80, 90, 100] as const;
